/**
 * Arborescence des dossiers pour l'affichage (barre laterale, listes de choix) : chaque dossier suit son
 * parent, ses sous-dossiers juste dessous, un cran plus loin -- comme Bitwarden et 1Password.
 *
 * L'ordre des freres est celui recu (`VaultStore.listFolders` trie par nom). Un dossier dont le parent
 * manque (supprime, pas encore synchronise) s'affiche a la racine ; un cycle `parentId` (deux appareils
 * qui deplacent chacun un dossier sous l'autre, puis fusion) n'est jamais atteint depuis la racine :
 * il s'affiche aussi a la racine plutot que de disparaitre de l'ecran.
 */

export interface FolderTreeRow<T> {
  readonly folder: T;
  /** 0 = racine. */
  readonly depth: number;
}

export function folderTree<T extends { readonly id: string; readonly parentId: string | null }>(folders: readonly T[]): FolderTreeRow<T>[] {
  const ids = new Set(folders.map((folder) => folder.id));
  const children = new Map<string | null, T[]>();
  for (const folder of folders) {
    const parent = folder.parentId !== null && ids.has(folder.parentId) ? folder.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), folder]);
  }
  const rows: FolderTreeRow<T>[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number): void => {
    for (const folder of children.get(parent) ?? []) {
      if (seen.has(folder.id)) continue;
      seen.add(folder.id);
      rows.push({ folder, depth });
      walk(folder.id, depth + 1);
    }
  };
  walk(null, 0);
  for (const folder of folders) {
    if (seen.has(folder.id)) continue;
    seen.add(folder.id);
    rows.push({ folder, depth: 0 });
    walk(folder.id, 1);
  }
  return rows;
}

/** Profondeur affichee, bornee : au-dela, l'indentation mangerait le nom dans une barre de 240 px. */
export const MAX_FOLDER_INDENT = 5;

/**
 * Le dossier et tous ses descendants : ce sont les parents interdits quand on le deplace (le mettre sous
 * lui-meme ou sous un de ses enfants le ferait sortir de l'arbre). Un cycle deja present ne boucle pas.
 */
export function folderSubtreeIds<T extends { readonly id: string; readonly parentId: string | null }>(folders: readonly T[], id: string): Set<string> {
  const ids = new Set([id]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const folder of folders) {
      if (folder.parentId !== null && ids.has(folder.parentId) && !ids.has(folder.id)) {
        ids.add(folder.id);
        grew = true;
      }
    }
  }
  return ids;
}

/**
 * Glisser-deposer d'un dossier dans la barre laterale : `parentId` (un dossier, `null` = racine) peut-il le recevoir ?
 * Non sur lui-meme ni sous un descendant (cycle), ni sur son parent actuel (rien ne bougerait), ni pour un inconnu.
 */
export function folderDropAllowed<T extends { readonly id: string; readonly parentId: string | null }>(
  folders: readonly T[],
  draggedId: string,
  parentId: string | null,
): boolean {
  const dragged = folders.find((folder) => folder.id === draggedId);
  if (dragged === undefined || dragged.parentId === parentId) return false;
  return parentId === null || (folders.some((folder) => folder.id === parentId) && !folderSubtreeIds(folders, draggedId).has(parentId));
}

/**
 * Glisser une entree sur la barre laterale : le dossier `folderId` (`null` = hors dossier) peut-il la recevoir ?
 * Non sur son dossier actuel (rien ne bougerait), ni sur un dossier inconnu.
 */
export function entryDropAllowed<T extends { readonly id: string }>(
  folders: readonly T[],
  entryFolderId: string | null,
  folderId: string | null,
): boolean {
  if (entryFolderId === folderId) return false;
  return folderId === null || folders.some((folder) => folder.id === folderId);
}

export interface VisibleFolderRow<T> extends FolderTreeRow<T> {
  /** Le dossier a des sous-dossiers : la barre laterale lui met un chevron replier / deplier. */
  readonly hasChildren: boolean;
  readonly collapsed: boolean;
  /** Replie, et le dossier de la vue est cache dessous : la ligne le signale plutot que de le perdre. */
  readonly holdsActive: boolean;
  /** Rang parmi ses freres (1 = premier) et nombre de freres, lui compris : `aria-posinset` / `aria-setsize`. */
  readonly position: number;
  readonly setSize: number;
}

/**
 * Les lignes de `folderTree` que montre la barre laterale : les descendants d'un dossier replie sont caches
 * (comme le triangle de Bitwarden, le chevron de 1Password). Repose sur l'ordre de `folderTree` -- les
 * descendants suivent leur parent, plus profonds. Un identifiant replie sans enfant ne cache rien.
 */
export function visibleFolderTree<T extends { readonly id: string }>(
  rows: readonly FolderTreeRow<T>[],
  collapsed: ReadonlySet<string>,
  activeId: string | null,
): VisibleFolderRow<T>[] {
  const visible: Omit<VisibleFolderRow<T>, 'position' | 'setSize'>[] = [];
  for (let index = 0; index < rows.length; index++) {
    const row = rows[index]!;
    const hasChildren = (rows[index + 1]?.depth ?? -1) > row.depth;
    const isCollapsed = hasChildren && collapsed.has(row.folder.id);
    let holdsActive = false;
    let next = index + 1;
    if (isCollapsed) {
      for (; next < rows.length && rows[next]!.depth > row.depth; next++) {
        if (rows[next]!.folder.id === activeId) holdsActive = true;
      }
    }
    visible.push({ ...row, hasChildren, collapsed: isCollapsed, holdsActive });
    index = next - 1;
  }
  return withSiblingRank(visible);
}

/**
 * Rang de chaque ligne visible parmi ses freres (`aria-posinset` / `aria-setsize`), pour tout arbre affiche dans
 * l'ordre « parent puis ses descendants, plus profonds » -- dossiers comme etiquettes imbriquees.
 * Les freres d'une ligne visible le sont tous (leur parent est deplie) : ils vont de la ligne qui suit le parent
 * a celle qui precede la prochaine ligne moins profonde ; les plus profondes entre eux sont leurs descendants.
 */
export function withSiblingRank<R extends { readonly depth: number }>(visible: readonly R[]): (R & { readonly position: number; readonly setSize: number })[] {
  return visible.map((row, index) => {
    let start = index;
    while (start > 0 && visible[start - 1]!.depth >= row.depth) start--;
    let end = index;
    while (end + 1 < visible.length && visible[end + 1]!.depth >= row.depth) end++;
    const siblings = visible.slice(start, end + 1).filter((other) => other.depth === row.depth);
    return { ...row, position: siblings.indexOf(row) + 1, setSize: siblings.length };
  });
}

/**
 * La seule ligne de l'arbre que la Tabulation atteint (tabulation itinerante de l'ARIA APG : l'arbre est UN arret,
 * les fleches font le reste) : le dossier de la vue, sinon le dossier replie qui le cache, sinon le premier.
 */
export function folderTreeTabStop<T extends { readonly id: string }>(rows: readonly VisibleFolderRow<T>[], activeId: string | null): string | null {
  const row = rows.find((candidate) => candidate.folder.id === activeId) ?? rows.find((candidate) => candidate.holdsActive) ?? rows[0];
  return row?.folder.id ?? null;
}

/** Ce que fait une touche sur une ligne de dossier : donner le focus a une ligne, replier / deplier ou ouvrir celle-ci. */
export interface FolderTreeMove {
  readonly kind: 'focus' | 'collapse' | 'expand' | 'expand-siblings' | 'select';
  readonly id: string;
  /** `expand-siblings` : les freres replies a deplier (la ligne garde le focus). */
  readonly ids?: readonly string[];
}

/**
 * Le clavier dans l'arbre de la barre laterale, selon le motif « tree » de l'ARIA APG (comme l'arbre des
 * dossiers de 1Password et le Finder) : Bas / Haut = ligne visible suivante / precedente ; Origine / Fin = premiere
 * / derniere ; Droite deplie un dossier replie, puis descend sur son premier sous-dossier ; Gauche replie un
 * dossier deplie, sinon remonte a son parent ; `*` deplie tous les freres replies de la ligne ; Entree / Espace
 * ouvrent le dossier (la ligne n'est pas un bouton).
 * `null` = la touche n'a rien a faire ici (elle reste au navigateur).
 * Lit les lignes de `visibleFolderTree` : un sous-dossier cache n'est jamais atteint.
 */
export function folderTreeKey<T extends { readonly id: string }>(rows: readonly VisibleFolderRow<T>[], id: string, key: string): FolderTreeMove | null {
  const index = rows.findIndex((row) => row.folder.id === id);
  if (index === -1) return null;
  const row = rows[index]!;
  const focus = (target: VisibleFolderRow<T> | undefined): FolderTreeMove | null => (target === undefined ? null : { kind: 'focus', id: target.folder.id });
  switch (key) {
    case 'ArrowDown':
      return focus(rows[index + 1]);
    case 'ArrowUp':
      return focus(rows[index - 1]);
    case 'Home':
      return focus(rows[0]);
    case 'End':
      return focus(rows[rows.length - 1]);
    case 'ArrowRight':
      if (!row.hasChildren) return null;
      return row.collapsed ? { kind: 'expand', id } : focus(rows[index + 1]);
    case 'ArrowLeft': {
      if (row.hasChildren && !row.collapsed) return { kind: 'collapse', id };
      for (let parent = index - 1; parent >= 0; parent--) if (rows[parent]!.depth < row.depth) return focus(rows[parent]);
      return null;
    }
    case '*': {
      // Les freres vont de la ligne qui suit le parent a celle qui precede la prochaine ligne moins profonde.
      let start = index;
      while (start > 0 && rows[start - 1]!.depth >= row.depth) start--;
      let end = index;
      while (end + 1 < rows.length && rows[end + 1]!.depth >= row.depth) end++;
      const ids = rows.slice(start, end + 1).filter((other) => other.depth === row.depth && other.collapsed).map((other) => other.folder.id);
      return ids.length === 0 ? null : { kind: 'expand-siblings', id, ids };
    }
    case 'Enter':
    case ' ':
      return { kind: 'select', id };
    default:
      return null;
  }
}

/** Delai au-dela duquel une frappe recommence la recherche au lieu de la prolonger (comme une liste de choix native). */
export const FOLDER_TYPE_AHEAD_MS = 500;

/** Le texte de la recherche par frappe en cours et l'instant de sa derniere touche. */
export interface FolderTypeAhead {
  readonly text: string;
  readonly at: number;
}

/**
 * Recherche par frappe du motif « tree » de l'ARIA APG (Finder, explorateur de Windows) : la touche s'ajoute au texte
 * en cours si elle suit la precedente de moins de `FOLDER_TYPE_AHEAD_MS`, sinon elle le recommence. `null` = la touche
 * n'est pas une frappe de recherche : touche nommee (`ArrowDown`, `Enter`…), `*` (deplier les freres), `/` et `?`
 * (raccourcis de la page : rechercher, liste des raccourcis), et l'Espace en debut de recherche -- il ouvre le dossier ;
 * au milieu d'une recherche, il appartient au nom (« Mes comptes »).
 */
export function folderTypeAheadStep(previous: FolderTypeAhead | null, key: string, now: number): FolderTypeAhead | null {
  if (Array.from(key).length !== 1 || /[\p{Cc}*\/?]/u.test(key)) return null;
  const going = previous !== null && now - previous.at < FOLDER_TYPE_AHEAD_MS ? previous.text : '';
  if (key === ' ' && going === '') return null;
  return { text: going + key, at: now };
}

/** Comparaison de la recherche par frappe : sans casse ni accents (« e » trouve « Ecole » et « Ete »). */
function typeAheadFold(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('fr');
}

/**
 * La ligne visible ou mene la recherche par frappe, depuis la ligne `id` : la suivante dont le nom commence par le
 * texte, en reprenant au debut apres la derniere. Une premiere lettre part de la ligne SUIVANTE (une meme lettre
 * frappee de nouveau passe au dossier suivant) ; un texte plus long part de la ligne courante, qui reste si elle
 * convient encore (« C » puis « Cl » garde « Clients »). Une meme lettre repetee (« ccc ») fait defiler les
 * dossiers qui commencent par elle. `null` = aucun nom ne convient : le focus ne bouge pas.
 * Lit les lignes de `visibleFolderTree` : un sous-dossier cache n'est jamais atteint.
 */
export function folderTreeTypeAhead<T extends { readonly id: string }>(
  rows: readonly VisibleFolderRow<T>[],
  id: string,
  text: string,
  nameOf: (folder: T) => string,
): FolderTreeMove | null {
  const index = rows.findIndex((row) => row.folder.id === id);
  if (index === -1 || text === '') return null;
  const chars = Array.from(typeAheadFold(text));
  const repeated = chars.every((char) => char === chars[0]);
  const query = repeated ? chars[0]! : chars.join('');
  const from = repeated ? index + 1 : index;
  for (let step = 0; step < rows.length; step++) {
    const row = rows[(from + step) % rows.length]!;
    if (typeAheadFold(nameOf(row.folder).trimStart()).startsWith(query)) return { kind: 'focus', id: row.folder.id };
  }
  return null;
}
